import { TextFieldProps } from '@mui/material';
import noop from 'lodash/noop';
import { ChangeEvent, KeyboardEventHandler, useState } from 'react';
import { Kind } from '../../types/kind';
import { WithRequired } from '../../utils/withRequired';
import { formatInput } from '../StatsCard/utils';
import { StyledField } from './Field.styles';
import { calculateNewCursorPosition } from './utils';

type CurrencyFieldProps = WithRequired<TextFieldProps, 'onChange'> & {
  readonly value: string;
  readonly kind: Kind;
};

export function CurrencyField(props: CurrencyFieldProps) {
  const { onChange, value: inputValue } = props;
  const [rawInput, setRawInput] = useState(inputValue.replace(/[,]/gu, ''));

  const handleCurrencyChange: KeyboardEventHandler = (event) => {
    const inputElement = event.target as HTMLInputElement;
    const { key: keyPressed } = event;
    const { selectionStart, selectionEnd } = inputElement;

    const isDelete = keyPressed === 'Delete' || keyPressed === 'Backspace';

    if (selectionStart === null || selectionEnd === null) {
      return;
    }
    let rawValueUpdated = rawInput;

    if (
      rawInput.split('.')[1]?.length >= 2 &&
      !isDelete &&
      selectionEnd > inputElement.value.length - 3
    ) {
      return;
    }

    let adjustedCursorPosition = selectionStart;
    const commasBeforeStart =
      inputElement.value.slice(0, selectionStart).split(',').length - 1;
    const commasBeforeEnd =
      inputElement.value.slice(0, selectionEnd).split(',').length - 1;

    const rawSelectionStart = selectionStart - commasBeforeStart;
    const rawSelectionEnd = selectionEnd - commasBeforeEnd;

    // Number input
    if (/^[0-9]$/u.test(keyPressed)) {
      rawValueUpdated =
        rawInput.slice(0, rawSelectionStart) +
        keyPressed +
        rawInput.slice(rawSelectionEnd);
      adjustedCursorPosition += 1;
    }
    // Decimal point input
    if (keyPressed === '.' || keyPressed === ',') {
      if (rawInput.includes('.')) {
        return;
      }
      rawValueUpdated = `${rawInput.slice(0, rawSelectionStart)}.${rawInput.slice(
        rawSelectionEnd,
      )}`;
      adjustedCursorPosition += 1;
    }

    // Delete/Suppress keyPressed
    if (isDelete) {
      if (selectionStart === selectionEnd) {
        rawValueUpdated =
          rawInput.slice(0, rawSelectionStart - 1) +
          rawInput.slice(rawSelectionStart);
        adjustedCursorPosition -= 1;
      } else {
        rawValueUpdated =
          rawInput.slice(0, rawSelectionStart) +
          rawInput.slice(rawSelectionEnd);
      }
    }

    // For any other key press, let the default behavior happen
    if (!/^[0-9]|[.,]|Backspace|Delete$/u.test(keyPressed)) {
      return;
    }

    const formattedValue = formatInput(rawValueUpdated);

    const newCursorPosition = calculateNewCursorPosition({
      rawInput,
      formattedValue,
      cursorPosition: adjustedCursorPosition - commasBeforeStart,
      isBackspace: keyPressed === 'Backspace',
    });

    const eventCopy = { ...event } as unknown as ChangeEvent<HTMLInputElement>;
    eventCopy.target.value = formattedValue;
    onChange(eventCopy);
    setRawInput(rawValueUpdated);

    requestAnimationFrame(() => {
      inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  return (
    <StyledField
      {...props}
      onBlur={(event) => {
        const { onBlur } = props;
        const eventCopy = {
          ...event,
        };
        eventCopy.target.value = formatInput(rawInput);
        onChange(eventCopy as unknown as ChangeEvent<HTMLInputElement>);
        onBlur?.(eventCopy);
      }}
      onChange={noop}
      onKeyDown={handleCurrencyChange}
    />
  );
}
